// src/components/phases/ResultsPhase.js
import React, { memo } from 'react';
import Card, { WinningCard } from '../common/Card';
import PlayerName from '../PlayerName';
import './ResultsPhase.css';

/**
 * ResultsPhase Component
 * Displays the results after a round ends
 */
const ResultsPhase = memo(
  ({
    round,
    roundWinner,
    roundWinners,
    isDoubleAwardRound,
    winningSubmission,
    pointsAwarded,
    pointsBreakdown,
    previousBlackCard,
    scores,
    judge,
    roomCreatorPlayerId,
    resultsCountdown,
    maxRounds,
  }) => {
    const isFinalRound = round >= maxRounds;
    // ISSUE #271: RHP Judge Double Award picks 2 winning submissions in one round
    const hasMultipleWinners = isDoubleAwardRound && (roundWinners || []).length > 1;

    return (
      <div className="round-results">
        <h2>🏆 Round {round} Results</h2>
        {hasMultipleWinners && (
          <div className="winner-announcement double-award-announcement">
            <h3>⭐ Judge Double Award — 2 Winners!</h3>
            <div className="double-award-winners">
              {roundWinners.map((winner, index) => (
                <div className="double-award-winner" key={winner?.id || index}>
                  <div className="double-award-winner-name">
                    <PlayerName
                      player={winner}
                      roomCreatorPlayerId={roomCreatorPlayerId}
                      showOwner={true}
                      showCzar={false}
                    />
                  </div>
                  <div className="winner-score">Chips: {scores[winner?.id] || 0}</div>
                </div>
              ))}
            </div>
            <p>Both submissions win the pot independently!</p>
          </div>
        )}
        {!hasMultipleWinners && roundWinner && (
          <div className="winner-announcement">
            <h3>
              Winner:{' '}
              <PlayerName
                player={roundWinner}
                roomCreatorPlayerId={roomCreatorPlayerId}
                showOwner={true}
                showCzar={false}
              />
              !
            </h3>

            {/* Show the winning submission with the black card it was for */}
            {winningSubmission && previousBlackCard && (
              <div className="winning-combination">
                <div className="black-card-context">
                  <p className="context-label">For the card:</p>
                  <Card
                    card={previousBlackCard}
                    type="black"
                    variant="result-black"
                    showSelectionIndicator={false}
                  />
                </div>
                <div className="winning-cards">
                  <p className="winning-label">
                    The winning answer{(winningSubmission.cards || []).length > 1 ? 's' : ''}:
                  </p>
                  <div className="winning-submission">
                    {(winningSubmission.cards || []).map((card, index) => (
                      <WinningCard
                        key={index}
                        text={typeof card === 'string' ? card : card?.text || 'Invalid card'}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* #190: Dynamic points display */}
            <p>
              Excellent combination! +{pointsAwarded || 1} Buffalo Chip
              {pointsAwarded !== 1 ? 's' : ''}
              {pointsBreakdown?.pot > 0 && (
                <span className="pot-bonus">
                  {' '}
                  (1 base + {pointsBreakdown.pot} wagered Chip{pointsBreakdown.pot !== 1 ? 's' : ''}
                  )
                </span>
              )}
            </p>
            <div className="winner-score">
              Chips: {scores[roundWinner?.id] || scores[roundWinner?.socketId] || 0}
            </div>
          </div>
        )}
        <div className="next-round-info">
          {isFinalRound ? (
            <p>🎉 Final round complete, game over.</p>
          ) : (
            <p>⚖️ Next Judge: {judge?.username || 'Unknown'}</p>
          )}
          {resultsCountdown !== null && (
            <div className="countdown-display">
              <p className="countdown-text">
                {isFinalRound ? 'Final rankings in: ' : 'Next round starts in: '}
                <span className="countdown-number">{resultsCountdown}</span>
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }
);

export default ResultsPhase;
